/**
 * MarkeeMix Tools Categorization System
 * Centralized data file for all tools - drives mega menu, discovery, and related tools
 * Supports 500+ tools scalability
 */

// Tool Categories with Icons and Colors
export const TOOL_CATEGORIES = [
  {
    id: "seo",
    name: "SEO Tools",
    icon: "Search",
    color: "emerald",
    description: "Optimize your website for search engines",
    path: "/tools/seo"
  },
  {
    id: "content",
    name: "Content Tools",
    icon: "FileText",
    color: "blue",
    description: "Create and optimize content",
    path: "/tools/content"
  },
  {
    id: "youtube",
    name: "YouTube Tools",
    icon: "Youtube",
    color: "red",
    description: "Grow your YouTube channel",
    path: "/tools/youtube"
  },
  {
    id: "social",
    name: "Social Media",
    icon: "Share2",
    color: "pink",
    description: "Manage social media presence",
    path: "/tools/social-media"
  },
  {
    id: "design",
    name: "Design Tools",
    icon: "Palette",
    color: "orange",
    description: "Create stunning visuals",
    path: "/tools/design"
  },
  {
    id: "ai",
    name: "AI Tools",
    icon: "Sparkles",
    color: "purple",
    description: "AI-powered marketing tools",
    path: "/tools/content"
  },
  {
    id: "ads",
    name: "Ads & Marketing",
    icon: "Megaphone",
    color: "cyan",
    description: "Advertising and marketing tools",
    path: "/tools/ads"
  },
  {
    id: "developer",
    name: "Developer Tools",
    icon: "Code",
    color: "slate",
    description: "Tools for developers",
    path: "/tools/developer"
  },
  {
    id: "utilities",
    name: "Utilities",
    icon: "Wrench",
    color: "amber",
    description: "Handy utility tools",
    path: "/tools/utilities"
  }
];

// Subcategories for each main category
export const TOOL_SUBCATEGORIES = {
  seo: [
    { id: "keyword-research", name: "Keyword Research", icon: "Search" },
    { id: "technical-seo", name: "Technical SEO", icon: "Settings" },
    { id: "backlink-analysis", name: "Backlink Analysis", icon: "Link" },
    { id: "rank-tracking", name: "Rank Tracking", icon: "TrendingUp" },
    { id: "content-seo", name: "Content SEO", icon: "FileSearch" },
    { id: "local-seo", name: "Local SEO", icon: "MapPin" },
    { id: "competitor-analysis", name: "Competitor Analysis", icon: "Users" }
  ],
  content: [
    { id: "writing-tools", name: "Writing Tools", icon: "Edit" },
    { id: "content-optimization", name: "Content Optimization", icon: "Sparkles" },
    { id: "blog-tools", name: "Blog Tools", icon: "BookOpen" },
    { id: "grammar-tools", name: "Grammar & Readability", icon: "CheckCircle" },
    { id: "content-templates", name: "Templates", icon: "FileText" }
  ],
  youtube: [
    { id: "youtube-seo", name: "YouTube SEO", icon: "Search" },
    { id: "video-tools", name: "Video Tools", icon: "Video" },
    { id: "thumbnail-tools", name: "Thumbnail Tools", icon: "Image" },
    { id: "analytics", name: "Analytics", icon: "BarChart" }
  ],
  social: [
    { id: "hashtag-tools", name: "Hashtag Tools", icon: "Hash" },
    { id: "caption-tools", name: "Caption Tools", icon: "MessageSquare" },
    { id: "scheduling", name: "Scheduling", icon: "Calendar" },
    { id: "analytics", name: "Analytics", icon: "BarChart" }
  ],
  website: [
    { id: "website-analysis", name: "Website Analysis", icon: "Search" },
    { id: "performance", name: "Performance", icon: "Zap" },
    { id: "security", name: "Security", icon: "Shield" },
    { id: "monitoring", name: "Monitoring", icon: "Activity" }
  ],
  design: [
    { id: "image-tools", name: "Image Tools", icon: "Image" },
    { id: "color-tools", name: "Color Tools", icon: "Palette" },
    { id: "generators", name: "Generators", icon: "Wand" },
    { id: "pdf-tools", name: "PDF Tools", icon: "FileText" }
  ],
  ai: [
    { id: "ai-writing", name: "AI Writing", icon: "Edit" },
    { id: "ai-image", name: "AI Image", icon: "Image" },
    { id: "ai-marketing", name: "AI Marketing", icon: "Sparkles" }
  ],
  ads: [
    { id: "google-ads", name: "Google Ads", icon: "Chrome" },
    { id: "ad-tools", name: "Ad Tools", icon: "Target" }
  ],
  developer: [
    { id: "code-tools", name: "Code Tools", icon: "Code" },
    { id: "json-tools", name: "JSON Tools", icon: "Braces" },
    { id: "html-css", name: "HTML/CSS", icon: "FileCode" }
  ],
  utilities: [
    { id: "converters", name: "Converters", icon: "RefreshCw" },
    { id: "calculators", name: "Calculators", icon: "Calculator" },
    { id: "generators", name: "Generators", icon: "Wand" },
    { id: "text-tools", name: "Text Tools", icon: "Type" }
  ]
};

// All Tools with their categorization
export const ALL_TOOLS = [
  // ===== SEO TOOLS =====
  {
    id: "keyword-research",
    name: "Keyword Research",
    description: "Find profitable keywords with search volume and difficulty",
    category: "seo",
    subcategory: "keyword-research",
    path: "/tools/seo",
    icon: "Search",
    tags: ["keywords", "seo", "research", "volume"],
    isPremium: false,
    isNew: false,
    isFeatured: true
  },
  {
    id: "keyword-difficulty",
    name: "Keyword Difficulty Checker",
    description: "Analyze keyword competition and ranking difficulty",
    category: "seo",
    subcategory: "keyword-research",
    path: "/tools/seo",
    icon: "BarChart2",
    tags: ["keywords", "difficulty", "competition"],
    isPremium: true,
    isNew: false,
    comingSoon: true
  },
  {
    id: "keyword-cluster",
    name: "Keyword Clustering",
    description: "Group related keywords into topic clusters",
    category: "seo",
    subcategory: "keyword-research",
    path: "/tools/seo",
    icon: "GitBranch",
    tags: ["keywords", "clustering", "topics"],
    isPremium: true,
    isNew: false
  },
  {
    id: "serp-analyzer",
    name: "SERP Analyzer",
    description: "Analyze search engine results pages",
    category: "seo",
    subcategory: "keyword-research",
    path: "/tools/seo",
    icon: "Search",
    tags: ["serp", "analysis", "rankings"],
    isPremium: true,
    isNew: false,
    comingSoon: true
  },
  {
    id: "backlink-analyzer",
    name: "Backlink Analyzer",
    description: "Analyze backlinks and referring domains",
    category: "seo",
    subcategory: "backlink-analysis",
    path: "/tools/seo",
    icon: "Link",
    tags: ["backlinks", "domains", "authority"],
    isPremium: true,
    isNew: false
  },
  {
    id: "rank-tracking",
    name: "Rank Tracking",
    description: "Track keyword rankings over time",
    category: "seo",
    subcategory: "rank-tracking",
    path: "/tools/seo",
    icon: "TrendingUp",
    tags: ["rankings", "tracking", "positions"],
    isPremium: true,
    isNew: false
  },
  {
    id: "site-audit",
    name: "Site Audit",
    description: "Comprehensive SEO audit for your website",
    category: "seo",
    subcategory: "technical-seo",
    path: "/tools/seo",
    icon: "Search",
    tags: ["audit", "technical", "issues"],
    isPremium: false,
    isNew: false,
    isFeatured: true
  },
  {
    id: "technical-seo-tools",
    name: "Technical SEO Tools",
    description: "Robots.txt, Sitemap, Schema generators",
    category: "seo",
    subcategory: "technical-seo",
    path: "/tools/seo/technical",
    icon: "Settings",
    tags: ["technical", "robots", "sitemap", "schema"],
    isPremium: false,
    isNew: false
  },
  {
    id: "meta-tag-generator",
    name: "Meta Tag Generator",
    description: "Generate SEO-optimized meta tags",
    category: "seo",
    subcategory: "content-seo",
    path: "/free/meta-description-generator",
    icon: "Tag",
    tags: ["meta", "title", "description"],
    isPremium: false,
    isNew: false
  },
  {
    id: "competitor-analysis",
    name: "Competitor Analysis",
    description: "Analyze competitor SEO strategies",
    category: "seo",
    subcategory: "competitor-analysis",
    path: "/tools/seo",
    icon: "Users",
    tags: ["competitor", "analysis", "strategy"],
    isPremium: true,
    isNew: false
  },
  {
    id: "seo-dashboard",
    name: "SEO Dashboard",
    description: "Complete SEO management dashboard",
    category: "seo",
    subcategory: "technical-seo",
    path: "/tools/seo/dashboard",
    icon: "LayoutDashboard",
    tags: ["dashboard", "overview", "management"],
    isPremium: true,
    isNew: false
  },
  {
    id: "advanced-seo-suite",
    name: "Advanced SEO Suite",
    description: "Advanced SEO tools for professionals",
    category: "seo",
    subcategory: "technical-seo",
    path: "/tools/seo/advanced",
    icon: "Sparkles",
    tags: ["advanced", "professional", "suite"],
    isPremium: true,
    isNew: true
  },

  // ===== CONTENT TOOLS =====
  {
    id: "ai-writer",
    name: "AI Content Writer",
    description: "Generate high-quality content with AI",
    category: "content",
    subcategory: "writing-tools",
    path: "/tools/content",
    icon: "Edit",
    tags: ["ai", "writing", "content", "generation"],
    isPremium: false,
    isNew: false,
    isFeatured: true
  },
  {
    id: "rewriter",
    name: "AI Content Rewriter",
    description: "Rewrite and improve existing content",
    category: "content",
    subcategory: "writing-tools",
    path: "/tools/content",
    icon: "RefreshCw",
    tags: ["rewrite", "paraphrase", "improve"],
    isPremium: false,
    isNew: false
  },
  {
    id: "headline-analyzer",
    name: "Headline Analyzer",
    description: "Analyze and improve your headlines",
    category: "content",
    subcategory: "content-optimization",
    path: "/tools/content",
    icon: "Type",
    tags: ["headline", "title", "ctr"],
    isPremium: false,
    isNew: false
  },
  {
    id: "readability",
    name: "Readability Checker",
    description: "Check content readability score",
    category: "content",
    subcategory: "grammar-tools",
    path: "/tools/content",
    icon: "BookOpen",
    tags: ["readability", "score", "flesch"],
    isPremium: false,
    isNew: false
  },
  {
    id: "plagiarism",
    name: "Plagiarism & Grammar",
    description: "Check content for plagiarism and grammar",
    category: "content",
    subcategory: "grammar-tools",
    path: "/tools/content",
    icon: "Shield",
    tags: ["plagiarism", "duplicate", "original", "grammar"],
    isPremium: false,
    isNew: false
  },
  {
    id: "templates",
    name: "Content Templates",
    description: "Ready-to-use content templates",
    category: "content",
    subcategory: "content-templates",
    path: "/tools/content",
    icon: "FileText",
    tags: ["templates", "formats", "structures"],
    isPremium: false,
    isNew: true
  },
  {
    id: "calendar",
    name: "Content Calendar",
    description: "Plan and schedule your content",
    category: "content",
    subcategory: "blog-tools",
    path: "/tools/content",
    icon: "Calendar",
    tags: ["calendar", "planning", "schedule"],
    isPremium: true,
    isNew: false
  },
  {
    id: "topic-research",
    name: "Topic Research",
    description: "Find trending topics to write about",
    category: "content",
    subcategory: "blog-tools",
    path: "/tools/content",
    icon: "Compass",
    tags: ["topics", "research", "ideas"],
    isPremium: false,
    isNew: false
  },
  {
    id: "content-analyzer",
    name: "Content Analyzer",
    description: "Analyze content for SEO and readability",
    category: "content",
    subcategory: "content-optimization",
    path: "/tools/content",
    icon: "BarChart",
    tags: ["analyze", "seo", "readability"],
    isPremium: false,
    isNew: false
  },
  {
    id: "content-score",
    name: "Content Scoring",
    description: "SEO + Readability combined score",
    category: "content",
    subcategory: "content-optimization",
    path: "/tools/content",
    icon: "BarChart",
    tags: ["score", "seo", "readability"],
    isPremium: false,
    isNew: true
  },
  {
    id: "word-counter",
    name: "Word Counter",
    description: "Count words, characters, sentences",
    category: "content",
    subcategory: "writing-tools",
    path: "/free/word-counter",
    icon: "Hash",
    tags: ["word", "count", "characters"],
    isPremium: false,
    isNew: false
  },

  // ===== YOUTUBE TOOLS =====
  {
    id: "youtube-seo-tools",
    name: "YouTube SEO Tools",
    description: "Optimize your YouTube videos for search",
    category: "youtube",
    subcategory: "youtube-seo",
    path: "/tools/youtube",
    icon: "Youtube",
    tags: ["youtube", "seo", "optimization"],
    isPremium: false,
    isNew: false,
    isFeatured: true
  },
  {
    id: "youtube-title-generator",
    name: "YouTube Title Generator",
    description: "Generate catchy video titles",
    category: "youtube",
    subcategory: "youtube-seo",
    path: "/free/youtube-title-generator",
    icon: "Type",
    tags: ["youtube", "title", "generator"],
    isPremium: false,
    isNew: false
  },
  {
    id: "description-generator",
    name: "Description Generator",
    description: "Generate optimized video descriptions",
    category: "youtube",
    subcategory: "youtube-seo",
    path: "/tools/youtube",
    icon: "FileText",
    tags: ["youtube", "description", "generator"],
    isPremium: false,
    isNew: false
  },
  {
    id: "tag-generator",
    name: "YouTube Tag Generator",
    description: "Generate relevant video tags",
    category: "youtube",
    subcategory: "youtube-seo",
    path: "/tools/youtube",
    icon: "Tag",
    tags: ["youtube", "tags", "generator"],
    isPremium: false,
    isNew: false
  },
  {
    id: "video-downloader",
    name: "Video Downloader",
    description: "Download YouTube videos",
    category: "youtube",
    subcategory: "video-tools",
    path: "/free/video-downloader",
    icon: "Download",
    tags: ["youtube", "download", "video"],
    isPremium: false,
    isNew: false
  },
  {
    id: "thumbnail-downloader",
    name: "Thumbnail Downloader",
    description: "Download YouTube thumbnails",
    category: "youtube",
    subcategory: "thumbnail-tools",
    path: "/tools/youtube",
    icon: "Image",
    tags: ["youtube", "thumbnail", "download"],
    isPremium: false,
    isNew: false
  },
  {
    id: "channel-audit",
    name: "Channel Analyzer",
    description: "Analyze YouTube channel performance",
    category: "youtube",
    subcategory: "analytics",
    path: "/tools/youtube",
    icon: "BarChart",
    tags: ["youtube", "channel", "analytics"],
    isPremium: true,
    isNew: false
  },

  // ===== SOCIAL MEDIA TOOLS =====
  {
    id: "hashtag-generator",
    name: "Hashtag Generator",
    description: "Generate trending hashtags",
    category: "social",
    subcategory: "hashtag-tools",
    path: "/free/hashtag-generator",
    icon: "Hash",
    tags: ["hashtags", "social", "trending"],
    isPremium: false,
    isNew: false,
    isFeatured: true
  },
  {
    id: "instagram-image-downloader",
    name: "Instagram Image Downloader",
    description: "Download images from Instagram posts",
    category: "social",
    subcategory: "hashtag-tools",
    path: "/tools/social-media",
    icon: "Image",
    tags: ["instagram", "download", "images", "posts"],
    isPremium: false,
    isNew: true,
    isFeatured: true
  },
  {
    id: "caption-generator",
    name: "Caption Generator",
    description: "Generate engaging social captions",
    category: "social",
    subcategory: "caption-tools",
    path: "/tools/social-media",
    icon: "MessageSquare",
    tags: ["captions", "social", "engagement"],
    isPremium: false,
    isNew: false,
    comingSoon: true
  },
  {
    id: "scheduler",
    name: "Post Scheduler",
    description: "Schedule social media posts",
    category: "social",
    subcategory: "scheduling",
    path: "/tools/social-media",
    icon: "Calendar",
    tags: ["scheduler", "posts", "automation"],
    isPremium: true,
    isNew: false
  },
  {
    id: "social-analytics",
    name: "Social Analytics",
    description: "Analyze social media performance",
    category: "social",
    subcategory: "analytics",
    path: "/tools/social-media",
    icon: "BarChart",
    tags: ["analytics", "social", "performance"],
    isPremium: true,
    isNew: false,
    comingSoon: true
  },
  {
    id: "bio-generator",
    name: "Bio Generator",
    description: "Generate social media bios",
    category: "social",
    subcategory: "caption-tools",
    path: "/tools/social-media",
    icon: "User",
    tags: ["bio", "profile", "social"],
    isPremium: false,
    isNew: false
  },

  // ===== ADS & MARKETING TOOLS =====
  {
    id: "google-ads-tools",
    name: "Google Ads Tools",
    description: "Google advertising toolkit",
    category: "ads",
    subcategory: "google-ads",
    path: "/tools/google-ads",
    icon: "Chrome",
    tags: ["google", "ads", "marketing"],
    isPremium: true,
    isNew: false
  },
  {
    id: "ad-copy-generator",
    name: "Ad Copy Generator",
    description: "Generate compelling ad copy",
    category: "ads",
    subcategory: "ad-tools",
    path: "/tools/google-ads",
    icon: "Edit",
    tags: ["ads", "copy", "generator"],
    isPremium: false,
    isNew: false
  },
  // ===== DESIGN TOOLS =====
  {
    id: "design-tools",
    name: "Design Tools Suite",
    description: "Complete design toolkit",
    category: "design",
    subcategory: "image-tools",
    path: "/tools/design",
    icon: "Palette",
    tags: ["design", "images", "graphics"],
    isPremium: false,
    isNew: false
  },
  {
    id: "logo-maker",
    name: "Logo Maker",
    description: "Create professional logos",
    category: "design",
    subcategory: "image-tools",
    path: "/tools/design",
    icon: "Sparkles",
    tags: ["logo", "design", "brand"],
    isPremium: false,
    isNew: false
  },
  {
    id: "bg-remover",
    name: "Background Remover",
    description: "Remove backgrounds from images",
    category: "design",
    subcategory: "image-tools",
    path: "/tools/design",
    icon: "Image",
    tags: ["background", "remove", "image"],
    isPremium: false,
    isNew: false
  },
  {
    id: "qr-generator",
    name: "QR Code Generator",
    description: "Generate custom QR codes",
    category: "design",
    subcategory: "generators",
    path: "/tools/design",
    icon: "QrCode",
    tags: ["qr", "code", "generator"],
    isPremium: false,
    isNew: false
  },
  {
    id: "mockup-generator",
    name: "Mockup Generator",
    description: "Create product mockups",
    category: "design",
    subcategory: "generators",
    path: "/tools/design",
    icon: "Box",
    tags: ["mockup", "product", "design"],
    isPremium: true,
    isNew: false
  },
  {
    id: "color-palette",
    name: "Color Palette Generator",
    description: "Generate beautiful color palettes",
    category: "design",
    subcategory: "color-tools",
    path: "/tools/design",
    icon: "Palette",
    tags: ["color", "palette", "design"],
    isPremium: false,
    isNew: false
  },
  {
    id: "brand-kit",
    name: "Brand Kit Generator",
    description: "Generate complete brand kits",
    category: "design",
    subcategory: "generators",
    path: "/tools/design",
    icon: "Sparkles",
    tags: ["brand", "kit", "identity"],
    isPremium: true,
    isNew: true
  },
  {
    id: "pdf-tools",
    name: "PDF Tools",
    description: "Merge, split, compress PDFs",
    category: "design",
    subcategory: "pdf-tools",
    path: "/tools/design/pdf",
    icon: "FileText",
    tags: ["pdf", "merge", "compress"],
    isPremium: false,
    isNew: false
  },

  // ===== AI TOOLS =====
  {
    id: "ai-blog-generator",
    name: "AI Blog Generator",
    description: "Generate complete blog posts with AI",
    category: "ai",
    subcategory: "ai-writing",
    path: "/ai-blog",
    icon: "Sparkles",
    tags: ["ai", "blog", "generator"],
    isPremium: false,
    isNew: false,
    isFeatured: true
  },
  {
    id: "ai-image-generator",
    name: "AI Image Generator",
    description: "Generate images with AI",
    category: "ai",
    subcategory: "ai-image",
    path: "/ai-image-generator",
    icon: "Image",
    tags: ["ai", "image", "generator"],
    isPremium: true,
    isNew: true
  },

  // ===== WEBSITE TOOLS =====
  {
    id: "website-analyzer",
    name: "Website Analyzer",
    description: "Analyze website performance",
    category: "website",
    subcategory: "website-analysis",
    path: "/tools/seo",
    icon: "Globe",
    tags: ["website", "analysis", "performance"],
    isPremium: false,
    isNew: false,
    comingSoon: true
  },
  {
    id: "page-speed-checker",
    name: "Page Speed Checker",
    description: "Check website loading speed",
    category: "website",
    subcategory: "performance",
    path: "/tools/seo/technical",
    icon: "Zap",
    tags: ["speed", "performance", "loading"],
    isPremium: false,
    isNew: false,
    comingSoon: true
  },
  {
    id: "ssl-checker",
    name: "SSL Checker",
    description: "Check SSL certificate status",
    category: "website",
    subcategory: "security",
    path: "/tools/seo/technical",
    icon: "Shield",
    tags: ["ssl", "security", "certificate"],
    isPremium: false,
    isNew: false,
    comingSoon: true
  },

  // ===== UTILITIES =====
  {
    id: "json-formatter",
    name: "JSON Formatter",
    description: "Format and validate JSON",
    category: "utilities",
    subcategory: "converters",
    path: "/tools/developer",
    icon: "Braces",
    tags: ["json", "format", "validate"],
    isPremium: false,
    isNew: false
  },
  {
    id: "url-encoder",
    name: "URL Encoder/Decoder",
    description: "Encode and decode URLs",
    category: "utilities",
    subcategory: "converters",
    path: "/tools/developer",
    icon: "Link",
    tags: ["url", "encode", "decode"],
    isPremium: false,
    isNew: false
  },
  {
    id: "case-converter",
    name: "Case Converter",
    description: "Convert text case",
    category: "utilities",
    subcategory: "text-tools",
    path: "/tools/utilities",
    icon: "Type",
    tags: ["case", "text", "convert"],
    isPremium: false,
    isNew: false
  },
  {
    id: "lorem-ipsum-generator",
    name: "Lorem Ipsum Generator",
    description: "Generate placeholder text",
    category: "utilities",
    subcategory: "generators",
    path: "/tools/utilities",
    icon: "FileText",
    tags: ["lorem", "ipsum", "placeholder"],
    isPremium: false,
    isNew: false
  },
  {
    id: "qr-code-generator",
    name: "QR Code Generator",
    description: "Generate QR codes",
    category: "utilities",
    subcategory: "generators",
    path: "/tools/utilities",
    icon: "QrCode",
    tags: ["qr", "code", "generator"],
    isPremium: false,
    isNew: false
  }
];

// Helper function to get tools by category
export const getToolsByCategory = (categoryId) => {
  return ALL_TOOLS.filter(tool => tool.category === categoryId);
};

// Helper function to get tools by subcategory
export const getToolsBySubcategory = (categoryId, subcategoryId) => {
  return ALL_TOOLS.filter(
    tool => tool.category === categoryId && tool.subcategory === subcategoryId
  );
};

// Helper function to get related tools
export const getRelatedTools = (toolId, limit = 4) => {
  const tool = ALL_TOOLS.find(t => t.id === toolId);
  if (!tool) return [];
  
  return ALL_TOOLS
    .filter(t => 
      t.id !== toolId && 
      (t.category === tool.category || t.subcategory === tool.subcategory)
    )
    .slice(0, limit);
};

// Helper function to search tools
export const searchTools = (query) => {
  const lowerQuery = query.toLowerCase();
  return ALL_TOOLS.filter(tool =>
    tool.name.toLowerCase().includes(lowerQuery) ||
    tool.description.toLowerCase().includes(lowerQuery) ||
    tool.tags.some(tag => tag.includes(lowerQuery))
  );
};

// Helper function to get featured tools
export const getFeaturedTools = () => {
  return ALL_TOOLS.filter(tool => tool.isFeatured);
};

// Helper function to get new tools
export const getNewTools = () => {
  return ALL_TOOLS.filter(tool => tool.isNew);
};

// Navigation menu data for mega menu
export const MEGA_MENU_DATA = TOOL_CATEGORIES.map(category => ({
  ...category,
  subcategories: TOOL_SUBCATEGORIES[category.id] || [],
  tools: getToolsByCategory(category.id).filter(tool => !tool.comingSoon).slice(0, 8) // Top 8 tools per category, exclude coming soon
}));

// ===== USE-CASE BASED BROWSING =====
// Group tools by user goals/workflows
export const USE_CASES = [
  {
    id: "youtube-growth",
    name: "YouTube Growth",
    description: "Everything you need to grow your YouTube channel",
    icon: "Youtube",
    color: "red",
    gradient: "from-red-500 to-pink-500",
    tools: [
      "youtube-seo-tools",
      "youtube-title-generator",
      "youtube-description-generator", 
      "youtube-tag-generator",
      "thumbnail-downloader",
      "channel-analyzer",
      "video-downloader"
    ],
    tips: [
      "Research keywords before creating videos",
      "Optimize titles for CTR",
      "Use eye-catching thumbnails"
    ]
  },
  {
    id: "content-creation",
    name: "Content Creation",
    description: "Create high-quality content that ranks",
    icon: "FileText",
    color: "blue",
    gradient: "from-blue-500 to-cyan-500",
    tools: [
      "ai-writer",
      "rewriter",
      "headline-analyzer",
      "readability",
      "plagiarism",
      "templates",
      "topic-research",
      "content-analyzer",
      "content-score"
    ],
    tips: [
      "Start with keyword research",
      "Focus on readability",
      "Add internal links"
    ]
  },
  {
    id: "seo-audit",
    name: "SEO Audit & Analysis",
    description: "Complete website SEO analysis toolkit",
    icon: "Search",
    color: "emerald",
    gradient: "from-emerald-500 to-teal-500",
    tools: [
      "keyword-research",
      "site-audit",
      "backlink-analyzer",
      "rank-tracking",
      "competitor-analysis",
      "technical-seo-tools"
    ],
    tips: [
      "Run full site audit first",
      "Check backlink profile",
      "Monitor rank changes"
    ]
  },
  {
    id: "social-media-marketing",
    name: "Social Media Marketing",
    description: "Boost your social media presence",
    icon: "Share2",
    color: "pink",
    gradient: "from-pink-500 to-purple-500",
    tools: [
      "hashtag-generator",
      "post-scheduler",
      "bio-generator",
      "carousel-creator",
      "caption-generator",
      "instagram-profile-scraper"
    ],
    tips: [
      "Use trending hashtags",
      "Post at optimal times",
      "Engage with audience"
    ]
  },
  {
    id: "design-branding",
    name: "Design & Branding",
    description: "Create stunning visuals for your brand",
    icon: "Palette",
    color: "orange",
    gradient: "from-orange-500 to-amber-500",
    tools: [
      "design-tools",
      "brand-kit-generator",
      "image-compressor",
      "color-palette-generator",
      "pdf-tools",
      "qr-code-generator"
    ],
    tips: [
      "Keep designs consistent",
      "Use brand colors",
      "Optimize for each platform"
    ]
  },
  {
    id: "keyword-research-pro",
    name: "Keyword Research Pro",
    description: "Find the best keywords for your niche",
    icon: "TrendingUp",
    color: "violet",
    gradient: "from-violet-500 to-purple-500",
    tools: [
      "keyword-research",
      "keyword-compare",
      "keyword-cluster",
      "industry-keywords",
      "topic-research"
    ],
    tips: [
      "Target long-tail keywords",
      "Check competition level",
      "Group by intent"
    ]
  },
  {
    id: "local-business",
    name: "Local Business SEO",
    description: "Dominate local search results",
    icon: "MapPin",
    color: "cyan",
    gradient: "from-cyan-500 to-blue-500",
    tools: [
      "site-audit",
      "keyword-research",
      "competitor-analysis"
    ],
    tips: [
      "Claim Google My Business",
      "Get local reviews",
      "Use location keywords"
    ]
  },
  {
    id: "ecommerce",
    name: "E-commerce Marketing",
    description: "Sell more with smart marketing tools",
    icon: "ShoppingCart",
    color: "amber",
    gradient: "from-amber-500 to-yellow-500",
    tools: [
      "keyword-research",
      "content-analyzer",
      "site-audit"
    ],
    tips: [
      "Optimize product pages",
      "Use structured data",
      "Build quality backlinks"
    ]
  },
  {
    id: "blog-writing",
    name: "Blog Writing",
    description: "Write engaging blog posts that rank",
    icon: "FileText",
    color: "indigo",
    gradient: "from-indigo-500 to-blue-500",
    tools: [
      "ai-writer",
      "topic-research",
      "headline-analyzer",
      "readability",
      "templates"
    ],
    tips: [
      "Research topics first",
      "Use catchy headlines",
      "Keep paragraphs short"
    ]
  },
  {
    id: "competitor-spy",
    name: "Competitor Analysis",
    description: "Spy on competitors and outrank them",
    icon: "Users",
    color: "slate",
    gradient: "from-slate-600 to-slate-800",
    tools: [
      "competitor-analysis",
      "backlink-analyzer",
      "keyword-research",
      "site-audit"
    ],
    tips: [
      "Find their top keywords",
      "Analyze their backlinks",
      "Study their content strategy"
    ]
  }
];

// Helper function to get tools for a use case
export const getToolsForUseCase = (useCaseId) => {
  const useCase = USE_CASES.find(uc => uc.id === useCaseId);
  if (!useCase) return [];
  
  return useCase.tools.map(toolId => 
    ALL_TOOLS.find(t => t.id === toolId)
  ).filter(Boolean);
};

// Helper function for advanced search with scoring
export const advancedSearchTools = (query) => {
  if (!query || query.length < 2) return [];
  
  const lowerQuery = query.toLowerCase();
  const words = lowerQuery.split(' ').filter(w => w.length > 1);
  
  return ALL_TOOLS.map(tool => {
    let score = 0;
    
    // Exact name match = highest score
    if (tool.name.toLowerCase() === lowerQuery) score += 100;
    // Name starts with query
    else if (tool.name.toLowerCase().startsWith(lowerQuery)) score += 80;
    // Name contains query
    else if (tool.name.toLowerCase().includes(lowerQuery)) score += 60;
    
    // Description match
    if (tool.description.toLowerCase().includes(lowerQuery)) score += 30;
    
    // Tag matches
    tool.tags.forEach(tag => {
      if (tag === lowerQuery) score += 50;
      else if (tag.includes(lowerQuery)) score += 20;
    });
    
    // Word matches for multi-word queries
    words.forEach(word => {
      if (tool.name.toLowerCase().includes(word)) score += 15;
      if (tool.tags.some(tag => tag.includes(word))) score += 10;
    });
    
    // Boost for featured/new tools
    if (tool.isFeatured) score += 5;
    if (tool.isNew) score += 3;
    
    return { ...tool, score };
  })
  .filter(tool => tool.score > 0)
  .sort((a, b) => b.score - a.score)
  .slice(0, 12);
};
